import React from 'react';

/**
 * One tinted box with an optional caption, a summary line and a row of actions: the shape the job
 * card's customer/vehicle panels use, shared so the booking drawer's own boxes are the same thing
 * rather than a near-miss of it. Behaviour is the host's - this only draws the box.
 */
interface IProps {
  /** CUSTOMER, VEHICLE, ... - in small uppercase. Left out where the content already says it. */
  caption?: React.ReactNode;
  /** Sits opposite the caption, or at the end of the summary line when there is no caption. */
  extra?: React.ReactNode;
  /** The summary line: a name, a plate and model, a select. */
  children?: React.ReactNode;
  /** The row of equal-width buttons under it. */
  actions?: React.ReactNode;
  style?: React.CSSProperties;
}

export const detailsPanelStyle: React.CSSProperties = {
  // The app has no global border-box reset, so a panel given width: 100% would otherwise be its
  // padding and border wider than its column and spill past it.
  boxSizing: 'border-box',
  background: '#f7fafd',
  border: '1px solid #e8eef5',
  borderRadius: 8,
  padding: 12,
};

export const detailsPanelCaptionStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: 'rgba(0, 0, 0, 0.45)',
};

export const DetailsPanel: React.FC<IProps> = ({ caption, extra, children, actions, style }) => (
  <section
    style={{
      ...detailsPanelStyle,
      // A column so the actions can be pushed to the foot: side by side, two panels of different
      // content heights would otherwise put their button rows at different heights.
      display: 'flex',
      flexDirection: 'column',
      ...style,
    }}
  >
    {caption && (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 8,
          minHeight: 22,
        }}
      >
        <span style={detailsPanelCaptionStyle}>{caption}</span>
        {extra}
      </div>
    )}
    {(children || (!caption && extra)) && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
        {!caption && extra}
      </div>
    )}
    {/* Equal widths, filling the box - they open detail, they do not drive the job. */}
    {actions && (
      <div style={{ display: 'flex', gap: 8, marginTop: 'auto', paddingTop: 12 }}>{actions}</div>
    )}
  </section>
);
